import React from 'react';

interface FreshdeskTicket {
  id: number;
  subject: string;
  description: string;
  status: number;
  priority: number;
  type: string | null;
  created_at: string;
  updated_at: string;
  due_by: string | null;
  requester_id: number;
  responder_id: number | null;
  tags: string[];
  custom_fields: any;
  url: string;
}

interface FreshdeskTicketsWidgetProps {
  tickets: FreshdeskTicket[];
  isLoading: boolean;
  maxHeight?: number | null;
}

const getStatusLabel = (status: number): string => {
  const statusMap: { [key: number]: string } = {
    2: 'Åpen',
    3: 'Venter',
    4: 'Løst',
    5: 'Lukket'
  };
  return statusMap[status] || `Status ${status}`;
};

const getPriorityLabel = (priority: number): string => {
  const priorityMap: { [key: number]: string } = {
    1: 'Lav',
    2: 'Medium',
    3: 'Høy',
    4: 'Kritisk'
  };
  return priorityMap[priority] || `Prioritet ${priority}`;
};

const getPriorityColor = (priority: number): string => {
  const colorMap: { [key: number]: string } = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-yellow-100 text-yellow-800',
    3: 'bg-orange-100 text-orange-800',
    4: 'bg-red-100 text-red-800'
  };
  return colorMap[priority] || 'bg-gray-100 text-gray-800';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('no-NO', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const FreshdeskTicketsWidget: React.FC<FreshdeskTicketsWidgetProps> = ({ tickets, isLoading, maxHeight }) => {
  return (
    <div 
      className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/40 overflow-hidden flex flex-col h-full" 
    >
      <div className="px-6 py-4 border-b border-slate-200/40 bg-gradient-to-r from-slate-50/50 to-blue-50/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            My tickets
          </h3>
          {tickets.length > 0 && (
            <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
              {tickets.length}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-slate-600">Henter tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm text-slate-600">Ingen åpne tickets</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <a
                key={ticket.id}
                href={ticket.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-200/60 p-4 hover:shadow-md hover:border-blue-300 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {ticket.subject || `Ticket #${ticket.id}`}
                    </h4>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPriorityColor(ticket.priority)}`}>
                        {getPriorityLabel(ticket.priority)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {getStatusLabel(ticket.status)}
                      </span>
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0 ml-2 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
                {ticket.description && (
                  <p className="text-xs text-slate-600 line-clamp-2 mt-2">
                    {ticket.description}
                  </p>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                  <span className="text-xs text-slate-500">
                    Opprettet: {formatDate(ticket.created_at)}
                  </span>
                  {ticket.due_by && (
                    <span className={`text-xs ${new Date(ticket.due_by) < new Date() ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                      Forfaller: {formatDate(ticket.due_by)}
                    </span>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
